import React, { useContext } from "react";

import {
  FluidImage,
  useScreenClass,
  Button,
  Banner,
  BannerDescription,
  BannerEyebrow,
  BannerLinks,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  P,
  FlexBox,
  Link,
  Typography,
  tokens,
  BoundedContent,
} from "@vp/swan";

//Utils
import { Translations } from "../utils/Translation";
import { Icons } from "../utils/Icons";
import { getScreenClass } from "../utils/screenClass";
import { isMailingServicesMpv } from "../utils/mailingServicesUtil";
import TrackingUtil from "../utils/tracking";
import { IssuesWrapper } from "@vp/gatsby-pages-issues-tracker-plugin";
import { DesignServicesContext } from "../contexts/DesignServicesContextProvider";
import { PageStaticDataContext } from "../contexts/PageStaticDataContextProvider";

const DesignServices = () => {
  const {
    locale,
    mpv: { mpvId },
  } = useContext(PageStaticDataContext);
  const { designServices } = useContext(DesignServicesContext);
  const { isDesktop } = getScreenClass(useScreenClass());
  const designServicesStatus = designServices?.designServicesStatus;
  const designServicesUrl = designServices?.designServicesUrl;

  if (!shouldRenderDesignServices(mpvId, locale, designServicesStatus, designServicesUrl)) {
    return <></>;
  }

  const icons = Icons.getInstance();
  const designServicesHeroImage = icons.getIcon("designServicesHeroImage");
  const translationInstance = Translations.getInstance();

  const designServicesEyebrowEntry = translationInstance.getTranslationEntry(
    "designServicesEyebrow"
  );
  const designServicesTitleEntry = translationInstance.getTranslationEntry("designServicesTitle");
  const designServicesDescriptionEntry = translationInstance.getTranslationEntry(
    "designServicesDescription"
  );
  const designServicesCTAEntry = translationInstance.getTranslationEntry("designServicesCTA");

  return (
    <BoundedContent>
      <Banner
        backgroundColor="strong"
        data-testid="hero"
        style={{
          borderRadius: tokens.SwanBaseBorderRadius400,
          marginBottom: tokens.SwanSemSpace10,
        }}
      >
        <BannerTextContainer>
          <BannerText>
            <IssuesWrapper
              targetNodes={[designServicesEyebrowEntry]}
              elementName="Design Services Banner Eyebrow"
            >
              <BannerEyebrow>{designServicesEyebrowEntry}</BannerEyebrow>
            </IssuesWrapper>

            <IssuesWrapper
              targetNodes={[designServicesTitleEntry]}
              elementName="Design Services Banner Title"
            >
              <BannerTitle>{designServicesTitleEntry}</BannerTitle>
            </IssuesWrapper>

            <IssuesWrapper
              targetNodes={[designServicesDescriptionEntry]}
              elementName="Design Services Banner Description"
            >
              <BannerDescription>
                <P>{designServicesDescriptionEntry}</P>
              </BannerDescription>
            </IssuesWrapper>

            <BannerLinks>
              <IssuesWrapper
                targetNodes={[designServicesCTAEntry]}
                elementName="Design Services Banner CTA text"
              >
                <Typography textAlign="center">
                  <Button
                    skin="secondary"
                    onClick={() => TrackingUtil.trackDesignServicesClick(mpvId, designServicesUrl!)}
                    data-section="Link to other Services"
                    data-position={0}
                    data-translation="Find out more"
                    render={props => (
                      <Link href={designServicesUrl} {...props}>
                        {props.children}
                      </Link>
                    )}
                  >
                    {designServicesCTAEntry}
                  </Button>
                </Typography>
              </IssuesWrapper>
            </BannerLinks>
          </BannerText>
        </BannerTextContainer>

        <FlexBox
          flexDirection="column"
          justifyContent="center"
          alignItems="flex-start"
          style={{ marginRight: isDesktop ? tokens.SwanSemSpace12 : tokens.SwanSemSpace2 }}
        >
          <FluidImage
            src={designServicesHeroImage?.cloudinaryImage?.transformedCloudinaryUrl}
            alt={
              designServicesHeroImage?.altTextOverride ||
              designServicesHeroImage?.cloudinaryImage?.alt
            }
            role="presentation"
            style={{
              width: "100%",
              height: "auto",
              maxWidth: "670px",
            }}
          />
        </FlexBox>
      </Banner>
    </BoundedContent>
  );
};

export const shouldRenderDesignServices = (
  mpvId: string,
  locale: string,
  designServicesStatus?: string,
  designServicesUrl?: string
): boolean => {
  return Boolean(
    !isMailingServicesMpv(mpvId, locale) &&
      !(locale.toLowerCase() === "en-in") &&
      designServicesUrl &&
      designServicesStatus === "Available"
  );
};

export default DesignServices;
